import React from 'react';
import { View, Text, Linking, Platform, TouchableOpacity, TextInput } from 'react-native';
import MetaRow from '../MetaRow';
import OpeningHoursRow from '../OpeningHoursRow';
import { isWebsiteLabel } from '../placeholder-config';
import { isAddressLabel } from '../utils/metaUtils';
import { pickSocialIcon } from '../utils/socialUtils';

export interface DetailsTabProps {
  meta: Array<{ label: string; value: any }> | undefined;
  editMode: boolean;
  displayedMeta: Array<{ label: string; value: any }> | undefined;
  setLocalMeta: React.Dispatch<React.SetStateAction<Array<{ label: string; value: any }>>>;
  tags?: string[];
  setTags?: (tags: string[]) => void;
  coordinates?: { lat: number; lng: number } | null;
  onCoordinatesChange?: (lat: number | null, lng: number | null) => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({ 
  meta, 
  editMode, 
  displayedMeta, 
  setLocalMeta,
  tags,
  setTags,
  coordinates,
  onCoordinatesChange
}) => {
  console.log('[DetailsTab] Render with:', {
    editMode,
    metaLength: meta?.length,
    displayedMetaLength: displayedMeta?.length,
    displayedMeta,
  });
  
  if (!editMode && (!displayedMeta || displayedMeta.length === 0) && (!tags || tags.length === 0)) {
    return <Text style={{ color: '#9CA3AF', fontSize: 14 }}>暂无详细信息</Text>;
  }
  
  const allMeta = displayedMeta || [];
  
  // 在非编辑模式下，过滤掉没有值的字段
  const filteredMeta = editMode ? allMeta : allMeta.filter(pair => {
    const hasValue = Array.isArray(pair.value) 
      ? pair.value.filter(v => String(v || '').trim().length > 0).length > 0 
      : String(pair.value || '').trim().length > 0;
    return hasValue;
  });
  
  const [latInput, setLatInput] = React.useState(coordinates?.lat ? String(coordinates.lat) : '');
  const [lngInput, setLngInput] = React.useState(coordinates?.lng ? String(coordinates.lng) : '');
  
  const latInputRef = React.useRef(latInput);
  const lngInputRef = React.useRef(lngInput);
  
  latInputRef.current = latInput;
  lngInputRef.current = lngInput;

  React.useEffect(() => {
    const newLat = coordinates?.lat;
    const currentLat = parseFloat(latInputRef.current);
    if (typeof newLat === 'number' && !Number.isNaN(newLat)) {
      // Only update if numerically different (prevents overwriting "2." with "2")
      if (newLat !== currentLat) {
        setLatInput(String(newLat));
      }
    } else {
      if (latInputRef.current !== '') {
        setLatInput('');
      }
    }
  }, [coordinates?.lat]);

  React.useEffect(() => {
    const newLng = coordinates?.lng;
    const currentLng = parseFloat(lngInputRef.current);
    if (typeof newLng === 'number' && !Number.isNaN(newLng)) {
      if (newLng !== currentLng) {
        setLngInput(String(newLng));
      }
    } else {
      if (lngInputRef.current !== '') {
        setLngInput('');
      }
    }
  }, [coordinates?.lng]);
  
  if (!editMode && filteredMeta.length === 0 && (!tags || tags.length === 0)) {
    return <Text style={{ color: '#9CA3AF', fontSize: 14 }}>暂无详细信息</Text>;
  }
  
  return (
    <>
      {[...filteredMeta]
        .map((pair, originalIdx) => ({ ...pair, originalIdx }))  // 先保存原始索引
        .sort((a, b) => {
          const rank = (p: any) => (isWebsiteLabel(String(p.label || '')) ? 1 : 0);
          return rank(a) - rank(b);
        })
        .map((pair, sortedIdx) => {
          const lbl = String(pair.label || '').trim();
          const lowerLabel = lbl.toLowerCase();
          const isWiki = lowerLabel === 'wikidata' || lowerLabel === 'wikipedia';
          const isWebsite = isWebsiteLabel(lbl) || isWiki;
          const isOpeningHours = lowerLabel === '營業時間' || lowerLabel === 'opening hours' || lowerLabel === 'opening_hours';
          const isStarRating = lowerLabel === '星級' || lowerLabel === 'star rating' || lowerLabel === 'rating' || lowerLabel === 'stars';

          if (isOpeningHours && !editMode) {
            return (
              <OpeningHoursRow 
                key={`meta-${pair.originalIdx}`}
                label={pair.label}
                value={pair.value}
              />
            );
          }

          return (
            <MetaRow
              key={`meta-${pair.originalIdx}`}
              label={pair.label}
              value={pair.value}
              editMode={editMode}
              isWebsite={isWebsite}
              isStarRating={isStarRating}
              pickSocialIcon={pickSocialIcon}
              openUrl={(href) => {
                if (!href) return;
                if (typeof window !== 'undefined') window.open(href, '_blank');
                else if (Linking && Linking.openURL) Linking.openURL(href);
              }}
              onChangeLabel={(nextLabel) => {
                setLocalMeta((prev) => {
                  const prevArr = (prev || []).slice();
                  const realIndex = prevArr.findIndex(
                    (it) => String(it.label) === String(pair.label) && !isAddressLabel(it.label)
                  );
                  const targetIndex = realIndex >= 0 ? realIndex : pair.originalIdx;
                  const next = prevArr.slice();
                  next[targetIndex] = { ...next[targetIndex], label: nextLabel };
                  return next;
                });
              }}
              onChange={(nextVal) => {
                setLocalMeta((prev) => {
                  const prevArr = (prev || []).slice();
                  const realIndex = prevArr.findIndex(
                    (it) => String(it.label) === String(pair.label) && !isAddressLabel(it.label)
                  );
                  const targetIndex = realIndex >= 0 ? realIndex : pair.originalIdx;
                  const next = prevArr.slice();
                  next[targetIndex] = { ...next[targetIndex], value: nextVal };
                  return next;
                });
              }}
              onDelete={() => {
                setLocalMeta((prev) => {
                  const prevArr = (prev || []).slice();
                  // 使用原始索引删除，而不是排序后的索引
                  return prevArr.filter((_, i) => i !== pair.originalIdx);
                });
              }}
            />
          );
        })}
      
      {/* 編輯模式：添加新欄位按鈕（帶預設選項） */}
      {editMode && (
        <View style={{ paddingVertical: 12, borderTopWidth: 1, borderTopColor: '#E5E7EB', marginTop: 8 }}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {/* 預設字段快捷按鈕 */}
            {[
              { label: '營業時間', value: '週一-週五: 09:00-18:00\n週六-週日: 10:00-17:00' },
              { label: '電話', value: '' },
              { label: '網站', value: '' },
              { label: '價格區間', value: '¥¥-¥¥¥' },
              { label: '建議時長', value: '1-2小時' },
              { label: '最佳季節', value: '春秋' },
              { label: '特色推薦', value: '' },
              { label: '交通方式', value: '' },
            ].map((preset, idx) => (
              <TouchableOpacity
                key={idx}
                onPress={() => {
                  console.log('[DetailsTab] Add preset field:', preset.label);
                  setLocalMeta((prev) => {
                    const prevArr = (prev || []).slice();
                    // 檢查是否已存在同名欄位
                    const exists = prevArr.some(p => p.label === preset.label);
                    if (exists) {
                      console.log('[DetailsTab] Field already exists:', preset.label);
                      return prev;
                    }
                    const newArr = [...prevArr, preset];
                    console.log('[DetailsTab] Updated meta:', newArr);
                    return newArr;
                  });
                }}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  backgroundColor: '#F3F4F6',
                  borderRadius: 6,
                  borderWidth: 1,
                  borderColor: '#E5E7EB',
                }}
              >
                <Text style={{ color: '#374151', fontSize: 13, fontWeight: '600' }}>+ {preset.label}</Text>
              </TouchableOpacity>
            ))}
            
            {/* 自定義欄位按鈕 */}
            <TouchableOpacity
              onPress={() => {
                console.log('[DetailsTab] Add custom field button clicked');
                setLocalMeta((prev) => {
                  const prevArr = (prev || []).slice();
                  const newArr = [...prevArr, { label: '自定義', value: '' }];
                  console.log('[DetailsTab] Updated meta:', newArr);
                  return newArr;
                });
              }}
              style={{
                paddingHorizontal: 12,
                paddingVertical: 6,
                backgroundColor: '#EEF2FF',
                borderRadius: 6,
                borderWidth: 1,
                borderColor: '#C7D2FE',
              }}
            >
              <Text style={{ color: '#4F46E5', fontSize: 13, fontWeight: '600' }}>+ 自定義</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* 標籤區域 (Tags) */}
      {(editMode || (tags && tags.length > 0)) && (
        <View style={{ marginTop: 24, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#E5E7EB' }}>
          <Text style={{ fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 12 }}>標籤</Text>
          
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {tags && tags.map((tag, idx) => (
              <View 
                key={`${tag}-${idx}`} 
                style={{ 
                  flexDirection: 'row', 
                  alignItems: 'center',
                  backgroundColor: '#F3F4F6',
                  borderRadius: 6,
                  paddingLeft: 10,
                  paddingRight: editMode ? 4 : 10,
                  paddingVertical: 6
                }}
              >
                <Text style={{ color: '#4B5563', fontSize: 13 }}>#{tag}</Text>
                {editMode && setTags && (
                  <TouchableOpacity
                    onPress={() => {
                      const newTags = tags.filter((_, i) => i !== idx);
                      setTags(newTags);
                    }}
                    style={{ padding: 4, marginLeft: 2 }}
                  >
                    <Text style={{ color: '#9CA3AF', fontSize: 14, fontWeight: 'bold' }}>×</Text>
                  </TouchableOpacity>
                )}
              </View>
            ))}
            
            {editMode && setTags && (
              <View style={{ minWidth: 100, maxWidth: 200 }}>
                <TextInput
                  placeholder="+ 添加標籤"
                  style={{
                    fontSize: 13,
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    backgroundColor: '#fff',
                    borderWidth: 1,
                    borderColor: '#E5E7EB',
                    borderRadius: 6,
                    color: '#111827'
                  }}
                  onSubmitEditing={(e) => {
                    const val = e.nativeEvent.text.trim();
                    if (val && (!tags || !tags.includes(val))) {
                      setTags([...(tags || []), val]);
                      e.currentTarget.clear();
                    }
                  }}
                  onBlur={(e) => {
                    // Optional: add on blur if not empty
                    const val = e.nativeEvent.text.trim();
                    if (val && (!tags || !tags.includes(val))) {
                      setTags([...(tags || []), val]);
                      e.currentTarget.clear();
                    }
                  }}
                />
              </View>
            )}
          </View>
        </View>
      )}

      {/* 坐標區域 (Coordinates) - 僅編輯模式顯示 */}
      {editMode && (
        <View style={{ marginTop: 24, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#E5E7EB' }}>
          <Text style={{ fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 12 }}>地理坐標</Text>
          
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>緯度 (Latitude)</Text>
              <TextInput
                value={latInput}
                placeholder="例如: 22.3193"
                keyboardType="numeric"
                style={{
                  borderWidth: 1,
                  borderColor: '#E5E7EB',
                  borderRadius: 6,
                  padding: 10,
                  fontSize: 14,
                  color: '#111827',
                  backgroundColor: '#fff'
                }}
                onChangeText={(txt) => {
                  setLatInput(txt);
                  if (onCoordinatesChange) {
                    const lat = txt ? parseFloat(txt) : null;
                    // Use lngInput (local state) instead of coordinates?.lng to avoid stale value
                    const lng = lngInput ? parseFloat(lngInput) : null;
                    // Only propagate if valid number or empty (to clear)
                    if (lat === null || !Number.isNaN(lat)) {
                      onCoordinatesChange(lat, Number.isNaN(lng as number) ? null : lng);
                    }
                  }
                }}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>經度 (Longitude)</Text>
              <TextInput
                value={lngInput}
                placeholder="例如: 114.1694"
                keyboardType="numeric"
                style={{
                  borderWidth: 1,
                  borderColor: '#E5E7EB',
                  borderRadius: 6,
                  padding: 10,
                  fontSize: 14,
                  color: '#111827',
                  backgroundColor: '#fff'
                }}
                onChangeText={(txt) => {
                  setLngInput(txt);
                  if (onCoordinatesChange) {
                    // Use latInput (local state) instead of coordinates?.lat to avoid stale value
                    const lat = latInput ? parseFloat(latInput) : null;
                    const lng = txt ? parseFloat(txt) : null;
                    // Only propagate if valid number or empty (to clear)
                    if (lng === null || !Number.isNaN(lng)) {
                      onCoordinatesChange(Number.isNaN(lat as number) ? null : lat, lng);
                    }
                  }
                }}
              />
            </View>
          </View>
        </View>
      )}
    </>
  );
};

export default DetailsTab;
